/**
 * core.content_translations / core.translation_dispatch service layer.
 *
 * Reads + on-demand requests + realtime subscriptions for the AI-translated
 * UGC pipeline introduced in 20260601001000_core_translation_platform.sql.
 *
 * This file is the bridge between the @pc/i18n React hook
 * `useTranslatedContent` and the database. Only @pc/db may import
 * supabase-js, per the monorepo rule.
 */

import { supabase } from '../client';

export interface ContentTranslation {
  entity_type: string;
  entity_id: string;
  field_name: string;
  target_locale: string;
  source_locale: string;
  content: string;
  content_format: 'markdown' | 'plain' | 'html';
  model: string;
  human_reviewed: boolean;
  created_at: string;
}

export interface TranslationSettingsPublic {
  enabled: boolean;
  supported_locales: string[];
}

/**
 * Fetch the translation for one (entity, locale) tuple. Returns null when
 * no row exists yet — caller decides whether to render the source text
 * with a "Translating…" indicator or to enqueue an on-demand request.
 */
export async function getContentTranslation(input: {
  entityType: string;
  entityId: string;
  fieldName?: string;
  targetLocale: string;
}): Promise<ContentTranslation | null> {
  const { data, error } = await (supabase() as any)
    .schema('core')
    .from('content_translations')
    .select(
      'entity_type, entity_id, field_name, target_locale, source_locale, content, content_format, model, human_reviewed, created_at',
    )
    .eq('entity_type', input.entityType)
    .eq('entity_id', input.entityId)
    .eq('field_name', input.fieldName ?? 'body')
    .eq('target_locale', input.targetLocale)
    .maybeSingle();
  if (error) throw error;
  return (data as ContentTranslation | null) ?? null;
}

/**
 * Ask the platform to translate this entity into `targetLocale` lazily.
 * The worker picks up the dispatch row within ~60s (cron cadence) and
 * inserts a content_translations row when done. Caller listens for it
 * via subscribeContentTranslation().
 *
 * Returns the dispatch UUID or null when:
 *   - the translation already exists (idempotent no-op)
 *   - the platform is disabled / circuit breaker tripped
 *   - the source locale equals the target locale
 */
export async function requestContentTranslation(input: {
  entityType: string;
  entityId: string;
  fieldName?: string;
  targetLocale: string;
}): Promise<string | null> {
  const { data, error } = await (supabase() as any)
    .schema('core')
    .rpc('rpc_request_translation', {
      p_entity_type: input.entityType,
      p_entity_id: input.entityId,
      p_field_name: input.fieldName ?? 'body',
      p_target_locale: input.targetLocale,
    });
  if (error) throw error;
  return (data as string | null) ?? null;
}

/**
 * Subscribe to INSERT events on core.content_translations for this entity.
 * The callback fires once per arriving translation. Returns an unsubscribe
 * function (call on unmount).
 *
 * NB: Supabase realtime requires the table to be in the `supabase_realtime`
 * publication for INSERT events. Add it via:
 *   alter publication supabase_realtime add table core.content_translations;
 * (Installed as a follow-up migration so the table can be created first.)
 */
export function subscribeContentTranslation(
  input: {
    entityType: string;
    entityId: string;
    fieldName?: string;
  },
  onInsert: (row: ContentTranslation) => void,
): () => void {
  const fieldName = input.fieldName ?? 'body';
  const channelName = `ct:${input.entityType}:${input.entityId}:${fieldName}`;
  const ch = (supabase() as any)
    .channel(channelName)
    .on(
      'postgres_changes',
      {
        event: 'INSERT',
        schema: 'core',
        table: 'content_translations',
        filter: `entity_id=eq.${input.entityId}`,
      },
      (payload: { new: ContentTranslation }) => {
        const row = payload.new;
        if (
          row.entity_type === input.entityType &&
          row.field_name === fieldName
        ) {
          onInsert(row);
        }
      },
    )
    .subscribe();

  return () => {
    try {
      (supabase() as any).removeChannel(ch);
    } catch {
      // best-effort
    }
  };
}

/**
 * Read the non-secret translation settings (enabled flag + supported
 * locales). Used by the language picker dropdown.
 */
export async function getTranslationSettingsPublic(): Promise<TranslationSettingsPublic> {
  const { data, error } = await (supabase() as any)
    .schema('core')
    .rpc('rpc_translation_settings_public');
  if (error) throw error;
  const row = Array.isArray(data) ? data[0] : data;
  if (!row) return { enabled: false, supported_locales: [] };
  return row as TranslationSettingsPublic;
}

/**
 * Update the calling user's preferred_locale. NULL clears the preference
 * (falls back to browser detection).
 */
export async function setPreferredLocale(localeOrNull: string | null): Promise<void> {
  const { error } = await (supabase() as any)
    .schema('core')
    .rpc('rpc_set_preferred_locale', { p_locale: localeOrNull });
  if (error) throw error;
}

// =========================================================================
// Platform-admin: translation_settings + recent dispatch errors
// =========================================================================

export interface TranslationSettingsAdmin {
  enabled: boolean;
  default_provider: 'openai' | 'deepl' | 'google' | 'noop';
  has_openai_key: boolean;
  openai_model: string;
  has_deepl_key: boolean;
  has_google_key: boolean;
  routing_rules: unknown;
  glossary: string[];
  locale_style_rules: Record<string, Record<string, unknown>>;
  supported_locales: string[];
  daily_spend_limit_usd: number;
  spend_today_usd: number;
  spend_today_resets_at: string;
  circuit_breaker_tripped: boolean;
  circuit_breaker_reason: string | null;
  circuit_breaker_tripped_at: string | null;
  pricing: Record<string, { input_per_1k_usd: number; output_per_1k_usd: number }>;
  updated_at: string;
  updated_by: string | null;
}

export async function adminGetTranslationSettings(): Promise<TranslationSettingsAdmin | null> {
  const { data, error } = await (supabase() as any)
    .schema('core')
    .rpc('rpc_translation_settings_admin_read');
  if (error) throw error;
  const row = Array.isArray(data) ? data[0] : data;
  return (row as TranslationSettingsAdmin | null) ?? null;
}

export interface TranslationSettingsPatch {
  enabled?: boolean;
  default_provider?: 'openai' | 'deepl' | 'google' | 'noop';
  /** Pass to rotate; pass '' to clear. Omit to leave unchanged. */
  openai_api_key?: string;
  openai_model?: string;
  deepl_api_key?: string;
  google_api_key?: string;
  glossary?: string[];
  locale_style_rules?: Record<string, Record<string, unknown>>;
  supported_locales?: string[];
  daily_spend_limit_usd?: number;
  routing_rules?: unknown;
  pricing?: Record<string, { input_per_1k_usd: number; output_per_1k_usd: number }>;
  /** When true, clears circuit_breaker_tripped + reason. */
  reset_circuit_breaker?: boolean;
}

export async function adminSetTranslationSettings(patch: TranslationSettingsPatch): Promise<void> {
  const { error } = await (supabase() as any)
    .schema('core')
    .rpc('rpc_translation_settings_admin_set', { p_changes: patch as unknown });
  if (error) throw error;
}

export interface TranslationDispatchRow {
  id: string;
  entity_type: string;
  entity_id: string;
  field_name: string;
  source_locale: string | null;
  target_locales: string[];
  status: 'pending' | 'running' | 'done' | 'error' | 'skipped';
  attempts: number;
  last_error: string | null;
  created_at: string;
  started_at: string | null;
  completed_at: string | null;
}

export async function adminListTranslationErrors(limit = 50): Promise<TranslationDispatchRow[]> {
  const { data, error } = await (supabase() as any)
    .schema('core')
    .from('translation_dispatch')
    .select(
      'id, entity_type, entity_id, field_name, source_locale, target_locales, status, attempts, last_error, created_at, started_at, completed_at',
    )
    .eq('status', 'error')
    .order('created_at', { ascending: false })
    .limit(limit);
  if (error) throw error;
  return (data ?? []) as TranslationDispatchRow[];
}

/**
 * Read the current user's preferred_locale (null if not set).
 * Called once on app boot to seed react-i18next.
 */
export async function getPreferredLocale(): Promise<string | null> {
  const { data, error } = await (supabase() as any)
    .schema('core')
    .from('users')
    .select('preferred_locale')
    .eq('id', (await (supabase() as any).auth.getUser()).data.user?.id ?? '00000000-0000-0000-0000-000000000000')
    .maybeSingle();
  if (error) throw error;
  return ((data as { preferred_locale: string | null } | null)?.preferred_locale) ?? null;
}
